import {
  ChangeDetectionStrategy,
  Component,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  WritableSignal,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { pick } from 'lodash-es';
import { keepCompareOrder } from '@veone/ngx-utilities';

import { TimeRange, TimeRangeName } from '~core/models/shared.models';
import { CalendarHeaderComponent } from './calendar-header/calendar-header.component';
import { DateService } from '~core/services/date.service';

@Component({
  standalone: false,
  selector: 'calendar',
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalendarComponent implements OnChanges, OnDestroy {
  protected readonly _dateService = inject(DateService);

  readonly calendarHeader = CalendarHeaderComponent;
  readonly keepCompareOrder = keepCompareOrder;
  readonly generateTimeRange = this._dateService.generateTimeRange;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  readonly TIME_RANGE_NAME = pick(TimeRangeName, [
    'TODAY',
    'ANYTIME',
    'CURRENT_WEEK',
    'CURRENT_MONTH',
    'CURRENT_YEAR',
    'CUSTOM'
  ]);

  timeRangeView = input<boolean>(false);
  date = input<string | null>(null);
  timeRange = input<TimeRange | null>(null);
  timeRangeName = input<TimeRangeName>(this.TIME_RANGE_NAME.TODAY);

  timeRangeChange = output<{ name: TimeRangeName; range: TimeRange }>();
  dateChange = output<string | null>();

  customTimeRange: WritableSignal<TimeRange | null> = signal(null);
  selectedTimeRangeName: WritableSignal<TimeRangeName> = signal(
    this.TIME_RANGE_NAME.TODAY
  );

  selectedDate: WritableSignal<Date | null> = signal(null);

  ngOnChanges(changes: SimpleChanges): void {
    const dayjs = this._dateService.instance;

    if ('date' in changes) {
      const dateValue = this.date();
      this.selectedDate.set(dateValue ? dayjs(dateValue).toDate() : null);
    }

    if ('timeRange' in changes) {
      if (this.timeRangeName() === this.TIME_RANGE_NAME.CUSTOM) {
        this.customTimeRange.set(this.timeRange());
      } else {
        this.customTimeRange.set(null);
      }
    }

    if ('timeRangeName' in changes) {
      this.selectedTimeRangeName.set(this.timeRangeName());
      if (
        this.timeRangeName() === this.TIME_RANGE_NAME.CUSTOM &&
        this.timeRange()
      ) {
        this.customTimeRange.set(this.timeRange());
      } else if (this.timeRangeName() !== this.TIME_RANGE_NAME.CUSTOM) {
        this.customTimeRange.set(null);
      }
    }
  }

  ngOnDestroy(): void {
    this.resetAllCalendars();
  }

  setTimeRangeName(value: TimeRangeName): void {
    this.selectedTimeRangeName.set(value);
    if (value === TimeRangeName.CUSTOM) return;

    this.customTimeRange.set(null);
    const timeRange = this._dateService.generateTimeRange(value);
    this.timeRangeChange.emit({ name: value, range: timeRange });
  }

  setCustomTimeRange(
    range: { start: Date | null; end: Date | null } | null
  ): void {
    const dayjs = this._dateService.instance;

    if (!range || !range.start || !range.end) {
      this.customTimeRange.set(null);
      return;
    }

    const timeRange: TimeRange = {
      start: dayjs(range.start).startOf('day').format(),
      end: dayjs(range.end).endOf('day').format()
    };

    this.customTimeRange.set(timeRange);
    this.selectedTimeRangeName.set(this.TIME_RANGE_NAME.CUSTOM);
    this.timeRangeChange.emit({
      name: this.TIME_RANGE_NAME.CUSTOM,
      range: timeRange
    });
  }

  handleSelectedChange(date: Date | null): void {
    const dayjs = this._dateService.instance;

    this.selectedDate.set(date);
    const formattedDate = date ? dayjs(date).format() : null;
    this.dateChange.emit(formattedDate);
  }

  resetAllCalendars(): void {
    this.selectedDate.set(null);
    this.selectedTimeRangeName.set(this.TIME_RANGE_NAME.TODAY);
    this.customTimeRange.set(null);
  }
}
